<template>
  <template v-if="page">
    <BlogDesignEditorial
      :post="page"
      :content-path="contentPath"
      toc-style="rule-boxed"
      conversion-label-prefix="Blog end-of-article"
      with-also-read
      with-mid-cta
    />
    <!-- Long posts ran 8-22 phone screens without a start button; the bar shows once the title scrolls away. -->
    <PhoneStickyCta label="Blog" :location="$route.path" watch=".ed-hero" />
  </template>

  <main v-else class="not-found grow">
    <div class="not-found__card">
      <h1>Article not found</h1>
      <p>This resource may have moved.</p>
      <NuxtLink :to="localePath('/blog')">Back to resources</NuxtLink>
    </div>
  </main>
</template>

<script setup>
import { localizedUrl, stripLocaleFromPath } from '~/data/locales';
import { ogImagePathForRoute } from '~/utils/og-image';
import { rankingAnchor, rankingHeadingIds } from '~/utils/ranking-anchor.mjs';
import BlogDesignEditorial from '~/components/blog-designs/BlogDesignEditorial.vue';

const route = useRoute();
const { canonicalLocaleCode, isCurrentPageIndexable, localePath } = useWorkspaceLocale();
const contentPath = stripLocaleFromPath(route.path);
const siteUrl = 'https://workspace369.com';

const { data: page } = await useAsyncData(`content-${contentPath}`, () => {
  return queryContent(contentPath).findOne();
});

const currentYear = new Date().getFullYear();
const fallbackTitle = 'Workspace369 Resources';
const fallbackDescription = 'Workspace369 resources for service businesses.';

useHead(() => {
  const title = page.value?.title || fallbackTitle;
  const description = page.value?.description || fallbackDescription;
  const headCanonicalPath = page.value?._path || contentPath;
  const canonicalUrl = localizedUrl(siteUrl, headCanonicalPath, canonicalLocaleCode.value);
  const keywords = Array.isArray(page.value?.tags) ? page.value.tags.join(', ') : '';
  const image = `${siteUrl}${page.value?.image || ogImagePathForRoute(headCanonicalPath)}`;
  const imageAlt = `${title} - Workspace369 client-work guide`;
  // Quick answers are FAQs shown near the top; they join the post's FAQPage (only on posts
  // that have one), ahead of the end-of-post FAQs and never twice.
  const quickItems = Array.isArray(page.value?.quickAnswers) ? page.value.quickAnswers : [];
  const endFaqItems = Array.isArray(page.value?.faq) ? page.value.faq : [];
  const quickQuestions = new Set(quickItems.map((item) => item.question?.trim().toLowerCase()));
  const faqItems = endFaqItems.length
    ? [...quickItems, ...endFaqItems.filter((item) => !quickQuestions.has(item.question?.trim().toLowerCase()))]
    : [];
  const sourceItems = Array.isArray(page.value?.sources) ? page.value.sources : [];
  const wordCount = Number(page.value?.readingTime?.words || 0);
  const reviewer = page.value?.reviewer;
  const authorName = page.value?.author?.name || 'Workspace369';
  const authorRole = page.value?.author?.role || '';
  const softwareRanking = Array.isArray(page.value?.softwareRanking)
    ? page.value.softwareRanking
    : [];
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BlogPosting',
        '@id': `${canonicalUrl}#article`,
        headline: title,
        description,
        url: canonicalUrl,
        inLanguage: canonicalLocaleCode.value,
        isAccessibleForFree: true,
        abstract: description,
        ...(wordCount > 0 ? { wordCount } : {}),
        datePublished: page.value?.createdAt,
        dateModified: page.value?.updatedAt || page.value?.createdAt,
        author: authorRole
          ? {
              '@type': 'Person',
              name: authorName,
              jobTitle: authorRole,
              ...(['Simon Dziak', 'Szymon Dziak'].includes(authorName)
                ? {
                    '@id': `${siteUrl}/about/#founder`,
                    url: `${siteUrl}/about/#founder`,
                    sameAs: ['https://simondziak.com/', 'https://www.linkedin.com/in/szymondziak/'],
                    image: `${siteUrl}/images/authors/simon-dziak-2026-11-480.jpg`,
                  }
                : {}),
              worksFor: {
                '@type': 'Organization',
                name: 'Workspace369',
                url: siteUrl,
              },
            }
          : {
              '@type': 'Organization',
              name: authorName,
              url: siteUrl,
            },
        ...(reviewer?.name
          ? {
              contributor: {
                '@type': 'Organization',
                name: reviewer.name,
                description: reviewer.role || 'Product accuracy review',
                url: siteUrl,
              },
            }
          : {}),
        ...(sourceItems.length
          ? {
              citation: sourceItems.map((source) => source.url).filter(Boolean),
            }
          : {}),
        publisher: {
          '@type': 'Organization',
          name: 'Workspace369',
          url: siteUrl,
          logo: {
            '@type': 'ImageObject',
            url: `${siteUrl}/favicon/android-chrome-512x512.png`,
          },
        },
        image,
        about: {
          '@id': `${siteUrl}/#software`,
        },
        mentions: [
          {
            '@id': `${siteUrl}/#software`,
          },
        ],
        mainEntityOfPage: {
          '@id': `${canonicalUrl}#webpage`,
        },
      },
    ],
  };

  if (faqItems.length) {
    schema['@graph'].push({
      '@type': 'FAQPage',
      '@id': `${canonicalUrl}#faq`,
      mainEntity: faqItems.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  // Each item needs its own on-site URL: its section anchor, or a workspace369.com URL from the
  // front matter. Posts that rank tools in a table without per-tool headings get no ItemList.
  const headingIds = rankingHeadingIds(page.value?.body);
  const usedUrls = new Set();
  const rankingItems = softwareRanking.map((item) => {
    const anchor = rankingAnchor(item, headingIds);
    const onSiteUrl = typeof item.url === 'string' && item.url.startsWith(`${siteUrl}/`) ? item.url : undefined;
    // Two tools sharing one heading ("HubSpot CRM and Harvest") can't share a URL.
    const url = [anchor && `${canonicalUrl}#${anchor}`, onSiteUrl].find((candidate) => candidate && !usedUrls.has(candidate));
    if (url) usedUrls.add(url);
    return { item, url };
  });
  if (rankingItems.length && rankingItems.every(({ url }) => url)) {
    schema['@graph'].push({
      '@type': 'ItemList',
      '@id': `${canonicalUrl}#software-ranking`,
      name: title,
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      itemListElement: rankingItems.map(({ item, url }) => ({
        '@type': 'ListItem',
        position: item.position,
        name: item.name,
        url,
      })),
    });
  }

  // seoTitle shortens the <title> tag only; the H1 and social titles keep the post title.
  const seoTitle = page.value?.seoTitle || page.value?.title || title;
  const brandedTitle = `${seoTitle} | Workspace369`;

  return {
    title: brandedTitle.length <= 60 ? brandedTitle : seoTitle,
    meta: [
      { name: 'title', content: title },
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      {
        key: 'robots',
        name: 'robots',
        content: isCurrentPageIndexable.value ? 'index, follow' : 'noindex, follow',
      },
      { name: 'author', content: page.value?.author?.name || 'Workspace369' },
      { name: 'copyright', content: '© ' + currentYear + ' Workspace369' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:image', content: image },
      { name: 'twitter:image:alt', content: imageAlt },
      { property: 'og:type', content: 'article' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: canonicalUrl },
      { property: 'og:image', content: image },
      { property: 'og:image:secure_url', content: image },
      { property: 'og:image:type', content: 'image/jpeg' },
      { property: 'og:image:width', content: '1200' },
      { property: 'og:image:height', content: '630' },
      { property: 'og:image:alt', content: imageAlt },
      { property: 'article:published_time', content: page.value?.createdAt },
      { property: 'article:modified_time', content: page.value?.updatedAt },
    ],
    link: [
      { key: 'canonical', rel: 'canonical', href: canonicalUrl },
      { key: 'blog-feed', rel: 'alternate', type: 'application/atom+xml', title: 'Workspace369 blog', href: `${siteUrl}/blog/feed.xml` },
    ],
    script: [
      {
        key: `blog-schema-${headCanonicalPath}`,
        type: 'application/ld+json',
        children: JSON.stringify(schema),
      },
    ],
  };
});
</script>

<style scoped>
.not-found {
  padding: 10rem 1rem 6rem;
}

.not-found__card {
  width: min(100% - 2rem, 40rem);
  margin: 0 auto;
  border: 1px solid #dbe7f0;
  border-radius: 0.75rem;
  background: #ffffff;
  padding: 2rem;
  text-align: center;
}

.not-found h1 {
  color: #0f172a;
  font-size: 2rem;
  font-weight: 900;
}

.not-found p {
  margin: 0.5rem 0 1rem;
  color: #475569;
}

.not-found a {
  color: #0e7490;
  font-weight: 800;
}

.dark .not-found__card {
  border-color: #243247;
  background: #0a1220;
}

.dark .not-found h1 {
  color: #ffffff;
}

.dark .not-found p {
  color: #cbd5e1;
}

.dark .not-found a {
  color: #67e8f9;
}
</style>
